import type { CSSProperties } from 'react'
import {
  cloneElement,
  isValidElement,
  useId,
  useMemo,
  type HTMLAttributes,
  type ReactNode,
  type RefObject,
} from 'react'
import clsx from 'clsx'
import Popover from '@features/shared/components/Popover'
import { ToggleSwitch } from './ToggleSwitch'
import { CheckIcon } from '../icons/icons'
import {
  POPOVER_MENU_ICON_SIZE_PX,
  PopoverMenu,
  PopoverMenuDivider,
  PopoverMenuRow,
} from '@features/shared/components/PopoverMenu'
import { COMPACT_POPOVER_CONTENT_CLASS } from '@features/shared/components/compactDropdownStyles'
import { measureMaxTextWidth } from '@features/shared/utils/measureTextWidth'
import { focusQuietly } from '../utils/quietFocus'

const DROPDOWN_MENU_MIN_WIDTH_PX = 180
const DROPDOWN_MENU_MAX_WIDTH_PX = 360
const DROPDOWN_MENU_ROW_FONT = '400 12px Inter Variable, system-ui, sans-serif'
const DROPDOWN_MENU_ROW_PADDING_X_PX = 24
const DROPDOWN_MENU_ICON_GAP_PX = 8
const DROPDOWN_MENU_TRAILING_WIDTH_PX = 32
const DROPDOWN_MENU_WIDTH_BUFFER_PX = 8

export interface MenuItemAction {
  type: 'action'
  id: string
  label: string
  icon?: ReactNode
  tone?: 'neutral' | 'warning' | 'danger'
  disabled?: boolean
  disabledReason?: string
  /** Removes the item entirely. Use disabled when it should remain visible but unavailable. */
  omitted?: boolean
  onClick: () => void
}

export interface MenuItemToggle {
  type: 'toggle'
  id: string
  label: string
  icon?: ReactNode
  tone?: 'neutral' | 'warning' | 'danger'
  disabled?: boolean
  disabledReason?: string
  /** Removes the item entirely. Use disabled when it should remain visible but unavailable. */
  omitted?: boolean
  checked: boolean
  onChange: (checked: boolean) => void
}

/** One choice among its section's options; the chosen one shows a check. */
export interface MenuItemOption {
  type: 'option'
  id: string
  label: string
  icon?: ReactNode
  disabled?: boolean
  disabledReason?: string
  /** Removes the item entirely. Use disabled when it should remain visible but unavailable. */
  omitted?: boolean
  selected: boolean
  onSelect: () => void
}

export type MenuItem = MenuItemAction | MenuItemToggle | MenuItemOption

export interface MenuSection {
  label?: string
  items: MenuItem[]
}

interface DropdownMenuProps {
  trigger: ReactNode
  sections: MenuSection[]
  open: boolean
  onOpenChange: (open: boolean) => void
  align?: 'left' | 'right'
  placement?: 'default' | 'right-start' | 'right'
  verticalAnchorRect?: DOMRect | null
  horizontalAnchorRect?: DOMRect | null
  boundaryRect?: DOMRect | null
  boundaryElementRef?: RefObject<HTMLElement | null>
  anchorElementRef?: RefObject<HTMLElement | null>
  /** Center the first option on its anchor row when opening to the side. */
  alignFirstItemToAnchor?: boolean
  boundaryPaddingPx?: number
  /** Space between the trigger and the menu (see Popover); negative overlaps them. */
  sideOffsetPx?: number
  portalZIndex?: CSSProperties['zIndex']
  className?: string
  /** Class for the element wrapping the trigger (see Popover). */
  triggerClassName?: string
  /** Class for the menu's outer wrapper, inline-block by default (see Popover). */
  wrapperClassName?: string
  /**
   * `click` (the default) toggles the menu from its trigger; `manual` leaves
   * opening to the caller (a menu that opens on hover, say). Clicking outside
   * and Escape close it either way.
   */
  triggerMode?: 'click' | 'manual'
}

export function DropdownMenu({
  trigger,
  sections,
  open,
  onOpenChange,
  align = 'left',
  placement,
  verticalAnchorRect,
  horizontalAnchorRect,
  boundaryRect,
  boundaryElementRef,
  anchorElementRef,
  alignFirstItemToAnchor = false,
  boundaryPaddingPx,
  sideOffsetPx,
  portalZIndex,
  className,
  triggerClassName,
  wrapperClassName,
  triggerMode,
}: DropdownMenuProps) {
  const menuId = useId()
  const triggerId = isValidElement<HTMLAttributes<HTMLElement>>(trigger)
    ? (trigger.props.id ?? `${menuId}-trigger`)
    : undefined
  const menuTrigger = isValidElement<HTMLAttributes<HTMLElement>>(trigger)
    ? cloneElement(trigger, {
        id: triggerId,
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': open ? menuId : undefined,
      })
    : trigger
  const changeOpen = (next: boolean) => {
    if (!next && triggerId && document.getElementById(menuId)?.contains(document.activeElement)) {
      const triggerElement = document.getElementById(triggerId)
      if (triggerElement) focusQuietly(triggerElement, { preventScroll: true })
    }
    onOpenChange(next)
  }
  const sortedSections = useMemo(
    () =>
      sections
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => !item.omitted),
        }))
        .filter((section) => section.items.length > 0),
    [sections]
  )
  const autoWidth = useMemo(() => {
    const items = sortedSections.flatMap((section) => section.items)
    if (items.length === 0) return DROPDOWN_MENU_MIN_WIDTH_PX

    const maxLabelWidth = measureMaxTextWidth(
      items.map((item) => item.label),
      DROPDOWN_MENU_ROW_FONT
    )
    const hasIcon = items.some((item) => item.icon)
    const hasTrailing = items.some((item) => item.type === 'toggle' || item.type === 'option')
    const chromeWidth =
      DROPDOWN_MENU_ROW_PADDING_X_PX +
      DROPDOWN_MENU_WIDTH_BUFFER_PX +
      (hasIcon ? POPOVER_MENU_ICON_SIZE_PX + DROPDOWN_MENU_ICON_GAP_PX : 0) +
      (hasTrailing ? DROPDOWN_MENU_TRAILING_WIDTH_PX : 0)

    return Math.min(
      DROPDOWN_MENU_MAX_WIDTH_PX,
      Math.max(DROPDOWN_MENU_MIN_WIDTH_PX, Math.ceil(maxLabelWidth + chromeWidth))
    )
  }, [sortedSections])

  return (
    <Popover
      trigger={menuTrigger}
      contentId={menuId}
      contentRole="menu"
      contentLabelledBy={triggerId}
      open={open}
      onOpenChange={changeOpen}
      placement={placement}
      align={align}
      verticalAnchorRect={verticalAnchorRect}
      horizontalAnchorRect={horizontalAnchorRect}
      boundaryRect={boundaryRect}
      boundaryElementRef={boundaryElementRef}
      anchorElementRef={anchorElementRef}
      alignContentItemSelector={alignFirstItemToAnchor ? 'button' : undefined}
      boundaryPaddingPx={boundaryPaddingPx}
      sideOffsetPx={sideOffsetPx}
      portalZIndex={portalZIndex}
      triggerClassName={triggerClassName}
      triggerMode={triggerMode}
      wrapperClassName={wrapperClassName}
      contentClassName={COMPACT_POPOVER_CONTENT_CLASS}
      showCaret={false}
      stopTriggerPropagation={align === 'right'}
    >
      <div className={clsx('min-w-[180px]', className)} style={{ minWidth: autoWidth }}>
        {sortedSections.map((section, sIdx) => (
          <div key={section.label ?? sIdx} role="group" aria-label={section.label}>
            {sIdx > 0 && <PopoverMenuDivider />}
            {section.label && (
              <div className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted">
                {section.label}
              </div>
            )}
            <PopoverMenu>
              {section.items.map((item) => {
                if (item.type === 'toggle') {
                  return (
                    <PopoverMenuRow
                      key={item.id}
                      role="menuitemcheckbox"
                      aria-checked={item.checked}
                      tabIndex={-1}
                      disabled={item.disabled}
                      title={item.disabled ? item.disabledReason : undefined}
                      onClick={(event) => {
                        event.stopPropagation()
                        if (!item.disabled) item.onChange(!item.checked)
                      }}
                      icon={item.icon}
                      tone={item.tone}
                      trailing={
                        <ToggleSwitch
                          presentation
                          checked={item.checked}
                          disabled={item.disabled}
                          onChange={item.onChange}
                          ariaLabel={item.label}
                        />
                      }
                    >
                      {item.label}
                    </PopoverMenuRow>
                  )
                }

                if (item.type === 'option') {
                  return (
                    <PopoverMenuRow
                      key={item.id}
                      role="menuitemradio"
                      tabIndex={-1}
                      aria-checked={item.selected}
                      disabled={item.disabled}
                      title={item.disabled ? item.disabledReason : undefined}
                      onClick={(event) => {
                        event.stopPropagation()
                        if (!item.disabled) {
                          item.onSelect()
                          changeOpen(false)
                        }
                      }}
                      icon={item.icon}
                      trailing={
                        item.selected ? (
                          <CheckIcon size={12} className="shrink-0 text-muted" aria-hidden="true" />
                        ) : null
                      }
                    >
                      {item.label}
                    </PopoverMenuRow>
                  )
                }

                return (
                  <PopoverMenuRow
                    key={item.id}
                    role="menuitem"
                    tabIndex={-1}
                    disabled={item.disabled}
                    title={item.disabled ? item.disabledReason : undefined}
                    onClick={(event) => {
                      event.stopPropagation()
                      if (!item.disabled) {
                        item.onClick()
                        changeOpen(false)
                      }
                    }}
                    icon={item.icon}
                    tone={item.tone}
                  >
                    {item.label}
                  </PopoverMenuRow>
                )
              })}
            </PopoverMenu>
          </div>
        ))}
      </div>
    </Popover>
  )
}
